import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Pages/Partner.css';

/* ══════════════════════════════════════
   GSAP LOADER
══════════════════════════════════════ */
function loadGSAP() {
  return new Promise((resolve) => {
    if (window.gsap && window.ScrollTrigger) { resolve(); return; }
    const s1 = document.createElement('script');
    s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
    s1.onload = () => {
      const s2 = document.createElement('script');
      s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';
      s2.onload = () => { window.gsap.registerPlugin(window.ScrollTrigger); resolve(); };
      document.head.appendChild(s2);
    };
    document.head.appendChild(s1);
  });
}

/* ══════════════════════════════════════
   PARTICLE CANVAS
══════════════════════════════════════ */
function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); let W, H, pts, raf;
    const resize = () => { W = c.width = c.offsetWidth; H = c.height = c.offsetHeight; };
    const make = () => ({ x: Math.random() * W, y: Math.random() * H, vx: (Math.random() - .5) * .25, vy: (Math.random() - .5) * .25, r: Math.random() * 1.3 + .3, a: Math.random() * .35 + .07 });
    const init = () => { resize(); pts = Array.from({ length: 55 }, make); };
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      pts.forEach(p => { p.x += p.vx; p.y += p.vy; if (p.x < 0) p.x = W; if (p.x > W) p.x = 0; if (p.y < 0) p.y = H; if (p.y > H) p.y = 0; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fillStyle = `rgba(47,128,237,${p.a})`; ctx.fill(); });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y, d = Math.sqrt(dx * dx + dy * dy);
        if (d < 90) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(47,128,237,${.1 * (1 - d / 90)})`; ctx.lineWidth = .5; ctx.stroke(); }
      }
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    window.addEventListener('resize', init);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', init); };
  }, []);
  return <canvas ref={ref} className="pt-canvas" aria-hidden />;
}

/* ══════════════════════════════════════
   SECTION HEADER
══════════════════════════════════════ */
function SectionHeader({ eyebrow, title, accent, sub }) {
  return (
    <div className="pt-section-header">
      {eyebrow && <span className="pt-eyebrow">{eyebrow}</span>}
      <h2 className="pt-section-title">
        {title} {accent && <span className="pt-accent">{accent}</span>}
      </h2>
      {sub && <p className="pt-section-sub">{sub}</p>}
    </div>
  );
}

/* ══════════════════════════════════════
   SVG ICONS
══════════════════════════════════════ */
const IconDual = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="24" r="10" stroke="currentColor" strokeWidth="2"/>
    <circle cx="32" cy="24" r="10" stroke="currentColor" strokeWidth="2"/>
    <path d="M22 18a10 10 0 0 1 0 12M26 18a10 10 0 0 0 0 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const IconModular = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6"  y="6"  width="14" height="14" rx="3" stroke="currentColor" strokeWidth="2"/>
    <rect x="28" y="6"  width="14" height="14" rx="3" stroke="currentColor" strokeWidth="2"/>
    <rect x="6"  y="28" width="14" height="14" rx="3" stroke="currentColor" strokeWidth="2"/>
    <rect x="28" y="28" width="14" height="14" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M20 13h8M20 35h8M13 20v8M35 20v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="2 2"/>
  </svg>
);
const IconGlobal = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="17" stroke="currentColor" strokeWidth="2"/>
    <path d="M7 24h34M24 7c-5 4-8 10-8 17s3 13 8 17M24 7c5 4 8 10 8 17s-3 13-8 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M10 16h28M10 32h28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 3"/>
  </svg>
);
const IconEthics = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 6l14 5v10c0 9-6 16-14 19C16 37 10 30 10 21V11l14-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M17 24l5 5 9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconTech = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="10" width="36" height="26" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M16 20l-4 4 4 4M32 20l4 4-4 4M26 18l-4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconReseller = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 14h32l-4 20H12L8 14z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M8 14l-2-6H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="16" cy="38" r="3" stroke="currentColor" strokeWidth="2"/>
    <circle cx="32" cy="38" r="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M20 26l4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconConsulting = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="14" r="7" stroke="currentColor" strokeWidth="2"/>
    <path d="M10 38c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M30 26l6 6-6 6M18 26l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconStartup = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 6C30 10 38 18 38 28a14 14 0 0 1-28 0c0-10 8-18 14-22z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M16 36c0-4.418 3.582-8 8-8s8 3.582 8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M24 18v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="24" cy="15" r="2" fill="currentColor" opacity="0.5"/>
  </svg>
);
const IconAssets = () => (
  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="6" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M8 11h12M8 15h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="20" cy="19" r="4" fill="currentColor" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M18.5 19l1 1 2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconTraining = () => (
  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 4L4 10l10 6 10-6-10-6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    <path d="M7 15v6a7 3 0 0 0 14 0v-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M22 10v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="22" cy="18" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);
const IconGTM = () => (
  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 22l5-6 4 3 6-9 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 8h4v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconPortal = () => (
  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="22" height="22" rx="3" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M3 9h22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="7" cy="6" r="1.2" fill="currentColor" opacity="0.5"/>
    <circle cx="11" cy="6" r="1.2" fill="currentColor" opacity="0.5"/>
    <path d="M8 15h12M8 19h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const IconSupport = () => (
  <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="14" cy="14" r="10" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M10 14a4 4 0 0 1 8 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M6 14H4M24 14h-2M14 4V6M14 22v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.5"/>
    <circle cx="14" cy="14" r="2" fill="currentColor" opacity="0.4"/>
  </svg>
);

/* ══════════════════════════════════════
   FORM COMPONENT
══════════════════════════════════════ */
function PartnerForm() {
  const [form, setForm] = useState({ name: '', company: '', email: '', type: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!form.name.trim() || !form.email.trim() || !form.type) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/partner.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitted(true);
        setForm({ name: '', company: '', email: '', type: '', message: '' });
      } else {
        setError(result.message || 'Failed to submit application');
      }
    } catch (err) {
      setError('Failed to send application. Please try again.');
      console.error('Form submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-form-success">
        <div className="pt-success-icon">🎉</div>
        <h3 className="pt-success-title">Application Received!</h3>
        <p className="pt-success-sub">Our partnerships team will reach out within 2 business days. We're excited to explore what we can build together.</p>
      </div>
    );
  }

  return (
    <form className="pt-form" onSubmit={submit}>
      <div className="pt-form-row">
        <div className="pt-field">
          <label className="pt-label">Your Name</label>
          <input className="pt-input" name="name" type="text" placeholder="Arjun Sharma" value={form.name} onChange={handle} required disabled={loading} />
        </div>
        <div className="pt-field">
          <label className="pt-label">Company / Organisation</label>
          <input className="pt-input" name="company" type="text" placeholder="Acme Corp" value={form.company} onChange={handle} required disabled={loading} />
        </div>
      </div>
      <div className="pt-field">
        <label className="pt-label">Work Email</label>
        <input className="pt-input" name="email" type="email" placeholder="you@company.com" value={form.email} onChange={handle} required disabled={loading} />
      </div>
      <div className="pt-field">
        <label className="pt-label">Partnership Type</label>
        <select className="pt-select" name="type" value={form.type} onChange={handle} required disabled={loading}>
          <option value="" disabled>Select a partnership type…</option>
          <option value="technology">Technology Partner</option>
          <option value="reseller">Reseller / Channel Partner</option>
          <option value="consulting">Consulting / System Integrator</option>
          <option value="startup">Startup / Innovation Partner</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div className="pt-field">
        <label className="pt-label">Tell us about your goals</label>
        <textarea className="pt-textarea" name="message" placeholder="What are you hoping to achieve through this partnership?" rows={4} value={form.message} onChange={handle} disabled={loading} />
      </div>
      
      {error && (
        <div className="pt-form-error" style={{ color: '#ef4444', padding: '12px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', fontSize: '14px' }}>
          {error}
        </div>
      )}
      
      <button type="submit" className="pt-form-submit" disabled={loading}>
        {loading ? 'Sending...' : 'Apply to Become a Partner'} <span>→</span>
      </button>
    </form>
  );
}

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
const Partners = () => {
  const pageRef = useRef(null);

  const whyReasons = [
    { icon: <IconDual />,    color: 'var(--blue)',   title: 'Dual Expertise: AI + Human', desc: 'We bring the best of both worlds — cutting-edge AI systems and on-demand human capabilities — enabling partners to offer complete solutions to their clients.' },
    { icon: <IconModular />, color: 'var(--cyan)',   title: 'Modular & Flexible Platform', desc: 'Our APIs and services are designed to integrate smoothly with existing workflows, platforms, and enterprise ecosystems.' },
    { icon: <IconGlobal />,  color: 'var(--purple)', title: 'Global Reach, Local Touch',   desc: 'With roots in India and a global delivery model, we help our partners scale across geographies with precision and cultural fluency.' },
    { icon: <IconEthics />,  color: 'var(--green)',  title: 'Transparent & Ethical',        desc: 'We build long-term, trust-based partnerships. No lock-ins, no surprises — just shared goals and mutual growth.' },
  ];

  const programs = [
    {
      num: '01', icon: <IconTech />, color: '#2f80ed', badge: '⚙️',
      title: 'Technology Partners',
      sub: 'Platform & Product Integration',
      desc: 'Integrate IncogniFlex AI/ops capabilities into your platform or tools and co-create next-generation solutions.',
      items: ['White-labeled AI models & APIs', 'Joint product development opportunities', 'Technical support & co-branding resources'],
    },
    {
      num: '02', icon: <IconReseller />, color: '#56ccf2', badge: '🛒',
      title: 'Reseller & Channel Partners',
      sub: 'Grow Your Business',
      desc: 'Offer IncogniFlex solutions to your network and earn competitive margins while expanding your service portfolio.',
      items: ['Flexible pricing & commission structures', 'Sales enablement kits', 'Dedicated partner success manager'],
    },
    {
      num: '03', icon: <IconConsulting />, color: '#7c6fe0', badge: '🏗️',
      title: 'Consulting & System Integrators',
      sub: 'Transform Your Clients',
      desc: 'Bundle our services into your transformation or digital consulting solutions with seamless integration support.',
      items: ['Integration-ready HaaS/AIaaS modules', 'Pre-built workflows for major industries', 'Custom onboarding & support'],
    },
    {
      num: '04', icon: <IconStartup />, color: '#f59e0b', badge: '🚀',
      title: 'Startup & Innovation Partners',
      sub: 'Go Further, Faster',
      desc: 'Are you a startup looking to scale smarter? We offer tech and talent infrastructure to help you move at startup speed.',
      items: ['Access to AIaaS APIs at startup pricing', 'On-demand HaaS talent pool', 'Mentorship & co-marketing opportunities'],
    },
  ];

  const tools = [
    { icon: <IconAssets />,   color: 'var(--blue)',   label: 'Co-branded marketing assets' },
    { icon: <IconTraining />, color: 'var(--cyan)',   label: 'Training & certification' },
    { icon: <IconGTM />,      color: 'var(--purple)', label: 'Joint go-to-market (GTM) strategies' },
    { icon: <IconPortal />,   color: 'var(--green)',  label: 'Partner portal & Slack community' },
    { icon: <IconSupport />,  color: 'var(--amber)',  label: 'Priority support & technical onboarding' },
  ];

  const stats = [
    { num: '4', suffix: '+', label: 'Partner Programs' },
    { num: '30', suffix: '+', label: 'Countries Reached' },
    { num: '48h', suffix: '', label: 'Onboarding SLA', raw: true },
    { num: '100', suffix: '%', label: 'No Lock-In Policy' },
  ];

  useEffect(() => {
    loadGSAP().then(() => {
      const { gsap, ScrollTrigger } = window;

      gsap.fromTo('.pt-hero-content > *',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.2 }
      );

      gsap.utils.toArray('.pt-section-header').forEach(el => {
        gsap.fromTo(el,
          { opacity: 0, y: 32 },
          { opacity: 1, y: 0, duration: 0.65, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 85%' }
          }
        );
      });

      gsap.fromTo('.pt-why-card',
        { opacity: 0, y: 50, scale: 0.96 },
        { opacity: 1, y: 0, scale: 1, duration: 0.65, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: '.pt-why-grid', start: 'top 80%' }
        }
      );

      gsap.utils.toArray('.pt-program-card').forEach((el, i) => {
        gsap.fromTo(el,
          { opacity: 0, x: i % 2 === 0 ? -50 : 50 },
          { opacity: 1, x: 0, duration: 0.65, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 82%' }
          }
        );
      });

      gsap.fromTo('.pt-tool-item',
        { opacity: 0, x: -30 },
        { opacity: 1, x: 0, duration: 0.5, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: '.pt-tools-list', start: 'top 82%' }
        }
      );

      gsap.fromTo('.pt-form-card',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out',
          scrollTrigger: { trigger: '.pt-form-card', start: 'top 80%' }
        }
      );

      gsap.fromTo('.pt-cta-inner > *',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out',
          scrollTrigger: { trigger: '.pt-cta-section', start: 'top 82%' }
        }
      );
    });

    return () => {
      if (window.ScrollTrigger) window.ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return (
    <div className="pt-page" ref={pageRef}>

      {/* ════════════════════ HERO ════════════════════ */}
      <section className="pt-hero">
        <ParticleField />
        <div className="pt-grid-bg" aria-hidden />
        <div className="pt-glow pt-glow-l" aria-hidden />
        <div className="pt-glow pt-glow-r" aria-hidden />
        <div className="pt-hero-inner">
          <div className="pt-hero-content">
            <span className="pt-hero-eyebrow">
              <span className="pt-eyebrow-dot" />
              Partner Program · IncogniFlex Services
            </span>
            <h1 className="pt-hero-title">
              Innovate Together.{' '}
              <span className="pt-hero-accent">Grow Together.</span>
            </h1>
            <p className="pt-hero-sub">
              At IncogniFlex Services, we believe in the power of collaboration. Whether you're a
              technology provider, consulting firm, system integrator, or growth-focused reseller
              — let's build something that creates real value for both our customers and yours.
            </p>
            <div className="pt-hero-tags">
              {['Technology Partners', 'Resellers', 'Consulting & SI', 'Startup Partners'].map(t => (
                <span key={t} className="pt-hero-tag">{t}</span>
              ))}
            </div>
            <div className="pt-hero-actions">
              <a href="#become-partner" className="pt-cta-btn primary">
                <span>Become a Partner</span>
                <span className="pt-btn-arrow">↓</span>
              </a>
              <a href="mailto:partner@incogniflex.com" className="pt-cta-btn secondary">
                Contact Partnerships Team
              </a>
            </div>
          </div>
        </div>
        <div className="pt-hero-cut" aria-hidden />
      </section>

      {/* ════════════════════ STATS BAR ════════════════════ */}
      <section className="pt-stats-section">
        <div className="pt-container">
          <div className="pt-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="pt-stat">
                <div className="pt-stat-num">{s.num}{s.suffix}</div>
                <div className="pt-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ WHY PARTNER ════════════════════ */}
      <section className="pt-why-section">
        <div className="pt-container">
          <SectionHeader
            eyebrow="🚀 Why Partner with Us?"
            title="Built for"
            accent="Mutual Growth"
            sub="Four core pillars that make IncogniFlex the right long-term partner for your business."
          />
          <div className="pt-why-grid">
            {whyReasons.map((r, i) => (
              <div key={i} className="pt-why-card" style={{ '--wc': r.color }}>
                <div className="pt-why-icon-wrap" style={{ borderColor: r.color + '44', background: r.color + '15', color: r.color }}>
                  {r.icon}
                </div>
                <h3 className="pt-why-title">{r.title}</h3>
                <p className="pt-why-desc">{r.desc}</p>
                <div className="pt-card-glow" style={{ background: r.color }} aria-hidden />
                <div className="pt-card-top-line" style={{ background: `linear-gradient(90deg, ${r.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ PROGRAMS ════════════════════ */}
      <section className="pt-programs-section">
        <div className="pt-container">
          <SectionHeader
            eyebrow="🤝 Partnership Programs"
            title="Find Your"
            accent="Partnership Type"
            sub="Four distinct paths — each designed to match where you are and where you want to go."
          />
          <div className="pt-programs-grid">
            {programs.map((p, i) => (
              <div key={i} className="pt-program-card" style={{ '--pc': p.color }}>
                <div className="pt-prog-top">
                  <div className="pt-prog-icon-wrap" style={{ borderColor: p.color + '44', background: p.color + '15', color: p.color }}>
                    {p.icon}
                  </div>
                  <div className="pt-prog-badge-wrap">
                    <span className="pt-prog-num" style={{ color: p.color, background: p.color + '15', borderColor: p.color + '35' }}>{p.num}</span>
                    <span className="pt-prog-emoji">{p.badge}</span>
                  </div>
                </div>
                <div className="pt-prog-body">
                  <h3 className="pt-prog-title">{p.title}</h3>
                  <span className="pt-prog-sub" style={{ color: p.color }}>{p.sub}</span>
                  <p className="pt-prog-desc">{p.desc}</p>
                  <ul className="pt-prog-list">
                    {p.items.map((item, ii) => (
                      <li key={ii} className="pt-prog-item">
                        <span className="pt-prog-check" style={{ color: p.color }}>✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <a href="#become-partner" className="pt-prog-cta" style={{ '--pc': p.color }}>
                  Apply as {p.title.split(' ')[0]} Partner <span>→</span>
                </a>
                <div className="pt-card-glow" style={{ background: p.color }} aria-hidden />
                <div className="pt-card-top-line" style={{ background: `linear-gradient(90deg, ${p.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ TOOLS & SUPPORT ════════════════════ */}
      <section className="pt-tools-section">
        <div className="pt-container">
          <div className="pt-tools-inner">
            <div className="pt-tools-left">
              <SectionHeader
                eyebrow="🛠️ Tools & Support"
                title="Everything You"
                accent="Need to Win"
                sub="From day one, you get access to a full partner toolkit — designed to accelerate your go-to-market."
              />
              <ul className="pt-tools-list">
                {tools.map((t, i) => (
                  <li key={i} className="pt-tool-item">
                    <div className="pt-tool-icon" style={{ borderColor: t.color + '44', background: t.color + '15', color: t.color }}>
                      {t.icon}
                    </div>
                    <span className="pt-tool-label">{t.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="pt-tools-visual">
              <div className="pt-tools-card">
                <div className="pt-tools-card-glow" aria-hidden />
                <div className="pt-partner-portal">
                  <div className="pt-portal-header">
                    <div className="pt-portal-dots">
                      <span style={{ background: '#ef4444' }} />
                      <span style={{ background: '#f59e0b' }} />
                      <span style={{ background: '#10b981' }} />
                    </div>
                    <span className="pt-portal-title">Partner Portal</span>
                  </div>
                  <div className="pt-portal-body">
                    <div className="pt-portal-stat">
                      <span className="pt-portal-stat-num">12</span>
                      <span className="pt-portal-stat-label">Active Deals</span>
                    </div>
                    <div className="pt-portal-divider" />
                    <div className="pt-portal-stat">
                      <span className="pt-portal-stat-num" style={{ color: 'var(--green)' }}>94%</span>
                      <span className="pt-portal-stat-label">CSAT</span>
                    </div>
                    <div className="pt-portal-divider" />
                    <div className="pt-portal-stat">
                      <span className="pt-portal-stat-num" style={{ color: 'var(--amber)' }}>3</span>
                      <span className="pt-portal-stat-label">Certifications</span>
                    </div>
                  </div>
                  <div className="pt-portal-tags">
                    <span className="pt-portal-tag" style={{ color: 'var(--cyan)', borderColor: 'rgba(86,204,242,0.3)', background: 'rgba(86,204,242,0.08)' }}>AIaaS API Access</span>
                    <span className="pt-portal-tag" style={{ color: 'var(--purple)', borderColor: 'rgba(124,111,224,0.3)', background: 'rgba(124,111,224,0.08)' }}>GTM Toolkit</span>
                    <span className="pt-portal-tag" style={{ color: 'var(--green)', borderColor: 'rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)' }}>Priority Support</span>
                    <span className="pt-portal-tag" style={{ color: 'var(--amber)', borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)' }}>Co-branding Kit</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ BECOME A PARTNER FORM ════════════════════ */}
      <section className="pt-apply-section" id="become-partner">
        <div className="pt-container">
          <SectionHeader
            eyebrow="📝 Become a Partner"
            title="Let's Build the"
            accent="Future Together"
            sub="Tell us a bit about you and your goals — our partnerships team will reach out with next steps within 48 hours."
          />
          <div className="pt-form-card">
            <div className="pt-form-card-glow" aria-hidden />
            <div className="pt-form-card-top-line" aria-hidden />
            <div className="pt-form-layout">
              <div className="pt-form-left">
                <h3 className="pt-form-heading">Apply to Partner</h3>
                <p className="pt-form-subheading">Fill in the form and we'll get back to you within 2 business days.</p>
                <div className="pt-form-contact-list">
                  <div className="pt-form-contact-item">
                    <span className="pt-form-contact-icon">📧</span>
                    <span>partner@incogniflex.com</span>
                  </div>
                  <div className="pt-form-contact-item">
                    <span className="pt-form-contact-icon">📍</span>
                    <span>Bengaluru HQ · Global Partner Network</span>
                  </div>
                </div>
                <div className="pt-form-links">
                  <a href="#" className="pt-form-link">
                    <span>📄</span> Download Partner Program PDF
                  </a>
                  <a href="mailto:partner@incogniflex.com?subject=Partnership Discovery Call" className="pt-form-link">
                    <span>📞</span> Schedule a Discovery Call
                  </a>
                </div>
              </div>
              <div className="pt-form-right">
                <PartnerForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ CTA ════════════════════ */}
      <section className="pt-cta-section">
        <div className="pt-cta-glow" aria-hidden />
        <div className="pt-cta-grid-bg" aria-hidden />
        <div className="pt-container">
          <div className="pt-cta-inner">
            <span className="pt-cta-eyebrow">
              <span className="pt-eyebrow-dot" />
              Win Together
            </span>
            <h2 className="pt-cta-title">
              Ready to <span className="pt-accent">Partner Up?</span>
            </h2>
            <p className="pt-cta-sub">
              No lock-ins. No surprises. Just shared goals, real results, and a team that's invested in your success.
            </p>
            <div className="pt-cta-actions">
              <a href="#become-partner" className="pt-cta-btn primary">
                <span>Apply Now</span>
                <span className="pt-btn-arrow">→</span>
              </a>
              <a href="mailto:partner@incogniflex.com" className="pt-cta-btn secondary">
                Email Partnerships Team
              </a>
            </div>
            <p className="pt-cta-note">partner@incogniflex.com · Response within 48 hours</p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Partners;